import { Suspense, useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useUser } from '@/contexts/UserContext'
import Loader from '@/components/loader'
import { apiFetch, setAuthToken } from '@/lib/api'
import { config } from '@/lib/config'

function SSOCallbackContent() {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const { fetchUser } = useUser()
    const [error, setError] = useState(null)
    const [message, setMessage] = useState(null)

    useEffect(() => {
        const authenticate = async () => {
            try {
                // The WSCIdentity cookie is set after SSO completes — fetch user data directly
                // from the WSC login service (same approach as bqpanel's completeLogin)
                const wscApiUrl = (config.apiUrl || '').replace(/\/kagami$/, '') || 'https://www.clipro.tv/api'
                const wscResponse = await fetch(`${wscApiUrl}/login/`, {
                    method: 'GET',
                    credentials: 'include',
                })

                const wscData = await wscResponse.json().catch(() => ({}))

                if (!wscResponse.ok) {
                    setError(wscData.error || 'Failed to get user info')
                    setMessage(wscData.message || null)
                    return
                }

                // Pass WSC user data to kagami backend to create a kagami session
                const response = await apiFetch('/api/auth/sso/callback', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ authData: wscData }),
                })
                const data = await response.json()

                if (!response.ok) {
                    setError(data.error || 'Authentication failed')
                    setMessage(data.message || null)
                    return
                }

                if (data.token) setAuthToken(data.token)
                await fetchUser()
                navigate('/show/episodes')
            } catch (err) {
                setError('An error occurred during authentication')
                setMessage(err.message)
            }
        }

        authenticate()
    }, [])

    return (
        <div className="h-screen flex items-center justify-center bg-background">
            {!error && <Loader status="Authenticating..." />}
            {error &&
                <div className="text-center p-8 bg-card rounded-lg border max-w-md">
                    <h1 className="text-xl font-semibold text-primary mb-2">{error}</h1>
                    {message && <p className="text-secondary-foreground text-sm">{message}</p>}
                    <button
                        onClick={() => navigate('/login')}
                        className="mt-4 text-sm text-secondary-foreground underline"
                    >
                        Back to login
                    </button>
            </div>}
        </div>
    )
}

export default function SSOCallbackPage() {
    return (
        <Suspense fallback={<div className="h-screen flex items-center justify-center bg-background"><Loader status="Loading..." /></div>}>
            <SSOCallbackContent />
        </Suspense>
    )
}
